import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/App.css";
import { Layout } from "@/components/Layout";
import { CookieConsent } from "@/components/CookieConsent";
import { AnalyticsRouteTracker } from "@/components/AnalyticsRouteTracker";
import Home from "@/pages/Home";

const MarketsPage = lazy(() => import("@/pages/MarketsPage"));
const GoldPage = lazy(() => import("@/pages/GoldPage"));
const AssetPage = lazy(() => import("@/pages/AssetPage"));
const ComparePage = lazy(() => import("@/pages/ComparePage"));
const Learn = lazy(() => import("@/pages/Learn"));
const Articles = lazy(() => import("@/pages/Articles"));
const Legal = lazy(() => import("@/pages/Legal"));
const Methodology = lazy(() => import("@/pages/Methodology"));
const About = lazy(() => import("@/pages/About"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const ToolsIndex = lazy(() => import("@/pages/tools/ToolsIndex"));
const GoldCalculator = lazy(() => import("@/pages/tools/GoldCalculator"));
const CryptoProfitCalculator = lazy(() => import("@/pages/tools/CryptoProfitCalculator"));
const IfYouInvested = lazy(() => import("@/pages/tools/IfYouInvested"));
const CurrencyConverter = lazy(() => import("@/pages/tools/CurrencyConverter"));
const DcaCalculator = lazy(() => import("@/pages/tools/DcaCalculator"));
const PercentageChange = lazy(() => import("@/pages/tools/PercentageChange"));
const InvestmentReturn = lazy(() => import("@/pages/tools/InvestmentReturn"));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function Fallback() {
  return <div className="flex min-h-[50vh] items-center justify-center text-sm text-muted-foreground">…</div>;
}

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <AnalyticsRouteTracker />
      <Layout>
        <Suspense fallback={<Fallback />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/markets" element={<MarketsPage />} />

            <Route path="/gold" element={<GoldPage />} />
            <Route path="/gold-price-per-gram" element={<GoldPage focus="gram" />} />
            <Route path="/gold-calculator" element={<Navigate to="/tools/gold-calculator" replace />} />
            <Route path="/silver" element={<AssetPage symbol="silver" />} />
            <Route path="/platinum" element={<AssetPage symbol="platinum" />} />

            <Route path="/bitcoin" element={<AssetPage symbol="bitcoin" />} />
            <Route path="/ethereum" element={<AssetPage symbol="ethereum" />} />
            <Route path="/crypto/:slug" element={<AssetPage kind="crypto" />} />

            <Route path="/oil/:slug" element={<AssetPage kind="oil" />} />
            <Route path="/energy/natural-gas" element={<AssetPage symbol="natgas" />} />

            <Route path="/forex/:slug" element={<AssetPage kind="forex" />} />

            <Route path="/tools" element={<ToolsIndex />} />
            <Route path="/tools/gold-calculator" element={<GoldCalculator />} />
            <Route path="/tools/crypto-profit-calculator" element={<CryptoProfitCalculator />} />
            <Route path="/tools/if-you-invested" element={<IfYouInvested />} />
            <Route path="/tools/currency-converter" element={<CurrencyConverter />} />
            <Route path="/tools/dca-calculator" element={<DcaCalculator />} />
            <Route path="/tools/percentage-change" element={<PercentageChange />} />
            <Route path="/tools/investment-return" element={<InvestmentReturn />} />

            <Route path="/compare" element={<ComparePage />} />
            <Route path="/compare/:slug" element={<ComparePage />} />

            <Route path="/learn" element={<Learn />} />
            <Route path="/learn/:slug" element={<Learn />} />
            <Route path="/articles" element={<Articles />} />
            <Route path="/articles/:slug" element={<Articles />} />

            <Route path="/methodology" element={<Methodology />} />
            <Route path="/about" element={<About />} />

            <Route path="/legal/disclaimer" element={<Legal type="disclaimer" />} />
            <Route path="/legal/privacy" element={<Legal type="privacy" />} />
            <Route path="/legal/cookies" element={<Legal type="cookies" />} />
            <Route path="/legal/terms" element={<Legal type="terms" />} />
            <Route path="/legal/methodology" element={<Methodology />} />

            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </Layout>
      <CookieConsent />
    </BrowserRouter>
  );
}

export default App;
